<template>
  <div>
    <div class="row mb-3">
      <div class="col-md-12">
        <div class="alert alert-info" v-if="solicitud.estado === 'AUTORIZADA'">
          <i class="fa fa-info-circle me-2"></i>
          La solicitud ha sido autorizada. La Unidad de Asuntos Administrativos debe certificar la disponibilidad POA/PAC.
        </div>
        <div class="alert alert-success" v-else-if="solicitud.estado === 'CERTIFICADA'">
          <i class="fa fa-check-circle me-2"></i>
          <strong>Solicitud certificada</strong> - El proceso de contratación cuenta con disponibilidad POA/PAC.
        </div>
      </div>
    </div>

    <!-- Formulario de certificación (solo UAA) -->
    <div class="row" v-if="(solicitud.estado === 'EN_CERTIFICACION' || solicitud.estado_certificacion === 'EN_CERTIFICACION') && hasRoleUAA()">
      <div class="col-md-12">
        <div class="card">
          <div class="card-header bg-light"><h6 class="mb-0">Certificación POA / PAC</h6></div>
          <div class="card-body">
            <div class="row">
              <div class="col-md-6">
                <div class="form-check mb-2">
                  <input type="checkbox" v-model="certificacion.certificado_poa" class="form-check-input" id="certPoa">
                  <label class="form-check-label" for="certPoa">
                    <strong>Certificar POA</strong> - La solicitud está registrada en el Plan Operativo Anual
                  </label>
                </div>
              </div>
              <div class="col-md-6">
                <div class="form-check mb-2">
                  <input type="checkbox" v-model="certificacion.certificado_pac" class="form-check-input" id="certPac">
                  <label class="form-check-label" for="certPac">
                    <strong>Certificar PAC</strong> - La solicitud está registrada en el Programa Anual de Contrataciones
                  </label>
                </div>
              </div>
              <div class="col-md-12">
                <div class="form-group mb-2">
                  <label class="form-label">Observaciones</label>
                  <textarea v-model="certificacion.observaciones" class="form-control" rows="2"></textarea>
                </div>
              </div>
              <div class="col-md-12">
                <BButton variant="success" @click="certificar" :disabled="!puedeCertificar">
                  <i class="fa fa-certificate me-1"></i> Certificar y Continuar
                </BButton>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Información de certificación existente -->
    <div class="row mt-3" v-if="certificacionExistente">
      <div class="col-md-12">
        <div class="card">
          <div class="card-header bg-light d-flex justify-content-between align-items-center">
            <h6 class="mb-0">Certificación Registrada</h6>
            <!-- Botón para generar PDF de Certificación Presupuestaria -->
            <BButton 
              variant="danger" 
              size="sm"
              @click="generarPdfCertificacion"
              :disabled="generandoPdf"
              title="Generar Certificación Presupuestaria (POA/PAC)"
            >
              <i v-if="generandoPdf" class="fa fa-spinner fa-spin"></i>
              <i v-else class="fa fa-file-pdf"></i> 
              Certificación POA/PAC
            </BButton>
          </div>
          <div class="card-body">
            <div class="row">
              <div class="col-md-6">
                <table class="table table-sm">
                  <tr><td width="40%"><strong>POA Certificado:</strong></td><td><i :class="certificacionExistente.certificado_poa ? 'fa fa-check-circle text-success' : 'fa fa-times-circle text-danger'"></i> {{ certificacionExistente.certificado_poa ? 'Sí' : 'No' }}</td></tr>
                  <tr><td><strong>PAC Certificado:</strong></td><td><i :class="certificacionExistente.certificado_pac ? 'fa fa-check-circle text-success' : 'fa fa-times-circle text-danger'"></i> {{ certificacionExistente.certificado_pac ? 'Sí' : 'No' }}</td></tr>
                  <tr><td><strong>Observaciones:</strong></td><td>{{ certificacionExistente.observaciones || 'N/A' }}</td></tr>
                  <tr><td><strong>Certificado por:</strong></td><td>{{ certificacionExistente.usuario_registro }} - {{ formatDate(certificacionExistente.fecha_registro) }}</td></tr>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import axios from 'axios';
import Swal from 'sweetalert2';
import { BButton } from 'bootstrap-vue-3';
import { useAuthStore } from '@/stores/authStore';

const props = defineProps({ solicitud: { type: Object, required: true } });
const emit = defineEmits(['refresh']);

const authStore = useAuthStore();
const certificacion = ref({ certificado_poa: false, certificado_pac: false, observaciones: '' });
const certificacionExistente = ref(null);
const generandoPdf = ref(false);

const hasRoleUAA = () => authStore.hasRole('bys');
//const puedeCertificar = computed(() => certificacion.value.certificado_poa || certificacion.value.certificado_pac);
const puedeCertificar = true; 

const fetchCertificacion = async () => {
  try {
    const response = await axios.get(`/certificacion/${props.solicitud.id}`);
    if (response.data.data) {
      certificacionExistente.value = response.data.data;
      if (props.solicitud.estado === 'CERTIFICADA') {
        certificacion.value = response.data.data;
      }
    }
  } catch (error) { console.error(error); }
};

const certificar = async () => {
  const result = await Swal.fire({
    title: '¿Certificar solicitud?',
    text: 'Esta acción confirmará la disponibilidad presupuestaria para la contratación.',
    icon: 'question', showCancelButton: true, confirmButtonText: 'Sí, certificar'
  });

  if (result.isConfirmed) {
    try {
      await axios.post(`/certificacion/${props.solicitud.id}`, certificacion.value);
      Swal.fire('Éxito', 'Solicitud certificada correctamente', 'success');
      emit('refresh');
      fetchCertificacion();
    } catch (error) {
      Swal.fire('Error', error.response?.data?.message || 'Error al certificar', 'error');
    }
  }
};

/**
 * Generar PDF de Certificación Presupuestaria (POA/PAC)
 */
const generarPdfCertificacion = async () => {
  generandoPdf.value = true;
  try {
    const response = await axios.get(`/certificacion/pdf/${props.solicitud.id}`, {
      responseType: 'blob'
    });
    
    // Crear URL del blob y descargar
    const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    const nombreArchivo = `Certificacion_Presupuestaria_${props.solicitud.codigo}.pdf`;
    
    link.href = blobUrl;
    link.setAttribute('download', nombreArchivo);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(blobUrl);
    
    Swal.fire('Éxito', 'Certificación presupuestaria generada correctamente', 'success');
  } catch (error) {
    console.error('Error al generar PDF:', error);
    Swal.fire('Error', error.response?.data?.message || 'No se pudo generar el PDF', 'error');
  } finally {
    generandoPdf.value = false;
  }
};

const formatDate = (date) => date ? new Date(date).toLocaleString('es-BO') : 'N/A';

onMounted(() => { fetchCertificacion(); });
</script>